import type { PrivateEvent } from '../../api/privateEvents';
import { WorkshopListOpenButton } from '../dashboards/primitives/WorkshopListOpenButton';

import IncompleteMarker from './IncompleteMarker';
import { formatDealValue, formatLocalDate, staffingLabel } from './privateEventDisplay';
import PrivateEventLocationCell from './PrivateEventLocationCell';

/**
 * One row of `PrivateEventsTable`. Its own file, per house rule.
 *
 * The open button sits inside the first cell rather than on the `<tr>`
 * itself — a `<button>` is not a valid direct child of `<tr>` — but the cell
 * is left unpositioned, so the button's absolute `inset: 0` resolves against
 * the row (which IS positioned) and still covers the whole row, not just the
 * one cell. That is what makes the row openable from the keyboard and gives
 * it an accessible name, rather than being a mouse-only affordance.
 */
export default function PrivateEventsTableRow({
  event,
  onOpen,
}: {
  event: PrivateEvent;
  onOpen?: (event: PrivateEvent) => void;
}) {
  const staffing = staffingLabel(event);
  return (
    <tr style={onOpen ? { position: 'relative', isolation: 'isolate' } : undefined}>
      <td className="nb-tabular" style={{ whiteSpace: 'nowrap', color: 'var(--nb-muted)' }}>
        {onOpen && (
          <WorkshopListOpenButton
            label={`Open ${event.clientName}`}
            radius={0}
            onActivate={() => onOpen(event)}
          />
        )}
        {formatLocalDate(event.localStartsAt, event.startsAt)}
      </td>
      <td>
        <span style={{ fontWeight: 700, color: 'var(--nb-ink)' }}>{event.clientName}</span>
        <span style={{ marginLeft: 8 }}>
          <IncompleteMarker event={event} />
        </span>
      </td>
      <td style={{ color: 'var(--nb-muted)' }}>
        <PrivateEventLocationCell event={event} />
      </td>
      <td className="nb-tabular" style={{ fontWeight: 700, color: 'var(--nb-ink)' }}>
        {event.headcount}
      </td>
      <td style={{ fontWeight: 600, color: staffing.color }}>{staffing.text}</td>
      <td className="nb-tabular" style={{ whiteSpace: 'nowrap', color: 'var(--nb-muted)' }}>
        {formatDealValue(event.dealValueCents)}
        {event.dealValueCents !== null && (
          <span style={{ marginLeft: 4, fontSize: 10, color: 'var(--nb-faint)' }}>est.</span>
        )}
      </td>
    </tr>
  );
}
